import React, { FunctionComponent, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  MenuItem,
  Stack,
  Drawer,
  SvgIcon,
  Typography,
  SelectChangeEvent,
} from "@mui/material";
import { PageHeader } from "../../components/PageHeader";
import { LegacyReportGrid } from "../../components/LegacyReportGrid";
import {
  Button,
  CustomBreadCrumbs,
  CustomDatePicker,
  CustomInput,
  CustomSelect,
} from "../../components";
import { getAxios } from "../../utils/endpoints";
import { DropdownValue } from "../../types/DropdownValue";
import { CustomAutocomplete } from "../../components/CustomAutocomplete";
import { formatDateHyphen, isValidDate } from "../../utils/time";
import Icon from "@mdi/react";
import { mdiFilterOutline } from "@mdi/js";
import { LegacyReportSidebar } from "../../components/LegacyReportSidebar";
import { RefreshOutlined } from "@mui/icons-material";

type IFilter = {
  label: string;
  type?: string;
};

type FilterData = {
  labelD1: string;
  labelD2: string;
  labelD3: string;
  labelD4: string;
  labelD5: string;
  labelD6: string;
  labelD7: string;
  labelD8: string;
  labelT1: string;
  labelT2: string;
  labelT3: string;
  labelT4: string;
  labelT5: string;
  labelT6: string;
  labelT7: string;
  labelT8: string;
  typeT1: string;
  typeT2: string;
  typeT3: string;
  typeT4: string;
  typeT5: string;
  typeT6: string;
  typeT7: string;
  typeT8: string;
  valuesD1: DropdownValue[];
  valuesD2: DropdownValue[];
  valuesD3: DropdownValue[];
  valuesD4: DropdownValue[];
  valuesD5: DropdownValue[];
  valuesD6: DropdownValue[];
  valuesD7: DropdownValue[];
  valuesD8: DropdownValue[];
};

type RelationData = {
  paramName: string;
  type: "T" | "D";
  label: string;
  value?: string | DropdownValue[] | null;
  selectedValue: string | Date;
};

const LegacyFilters: FunctionComponent<{
  filters: RelationData[];
  values: (string | Date)[];
  onChangeDate: (newValue: any, paramName: string) => void;
  onChangeSelect: (
    event: SelectChangeEvent<unknown>,
    paramName: string
  ) => void;
  onChangeInput: (
    event: React.ChangeEvent<HTMLInputElement>,
    paramName: string
  ) => void;
}> = (props): JSX.Element => {
  return (
    <Stack
      direction="row"
      justifyContent={props.filters.length > 3 ? "space-between" : ""}
      spacing={4}
      mt={4}
    >
      {props.filters.map((filter, index) => {
        if (filter.value === "D") {
          return (
            <Box>
              <CustomDatePicker
                width={200}
                onChange={(newValue) =>
                  props.onChangeDate(newValue, filter.paramName)
                }
                value={filter.selectedValue as Date}
                label={{ text: filter.label }}
              />
            </Box>
          );
        } else if (Array.isArray(filter.value)) {
          return (
            <CustomSelect
              width={200}
              onChange={(event) =>
                props.onChangeSelect(event, filter.paramName)
              }
              value={filter.selectedValue}
              label={filter.label}
            >
              {filter.value.map((item, index) => {
                return (
                  <MenuItem
                    sx={{
                      padding: item.description.trim() === "" ? 2 : undefined,
                    }}
                    value={item.id}
                    key={index}
                  >
                    {item.description}
                  </MenuItem>
                );
              })}
            </CustomSelect>
          );
        } else {
          return (
            <CustomInput
              onChange={(event) =>
                props.onChangeInput(
                  event as React.ChangeEvent<HTMLInputElement>,
                  filter.paramName
                )
              }
              value={props.values[index] as string}
              label={{ text: filter.label }}
            />
          );
        }
      })}
    </Stack>
  );
};

export const LegacyReport: FunctionComponent = () => {
  const [currentReport, setCurrentReport] =
    useState<DropdownValue | null>(null);
  const [legacyReports, setLegacyReports] = useState<DropdownValue[]>([]);
  const [legacyReport, setLegacyReport] = useState<any[] | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  const [filters, setFilters] = useState<RelationData[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [startIndex, setStartIndex] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(0);

  const [values, setValues] = useState<(Date | string)[]>([]);

  const onChangePage = (newIndex: number) => setStartIndex(newIndex);
  const onChangePageSize = (newPageSize: number) => setPageSize(newPageSize);

  const onChangeDate = (newValue: Date, paramName: string) => {
    const newArr = [...filters];

    const filter = newArr.find((value) => value.paramName === paramName);

    const index = newArr.indexOf(filter as RelationData);

    newArr[index].selectedValue = newValue as Date;

    setFilters(newArr);
  };

  const onChangeSelect = (
    event: SelectChangeEvent<unknown>,
    paramName: string
  ) => {
    const newArr = [...filters];

    const filter = newArr.find((value) => value.paramName === paramName);

    const index = newArr.indexOf(filter as RelationData);

    newArr[index].selectedValue = event.target.value as unknown as string;

    setFilters(newArr);
  };

  const onChangeInput = (
    event: React.ChangeEvent<HTMLInputElement>,
    paramName: string
  ) => {
    const newArr = [...filters];

    const filter = newArr.find((value) => value.paramName === paramName);

    const index = newArr.indexOf(filter as RelationData);

    newArr[index].selectedValue = event.target.value as string;

    setFilters(newArr);
  };

  const isGenerateButtonDisabled = (): boolean => {
    const DFilters = filters.filter((filter) => filter.type !== "T");

    console.log(DFilters);

    return DFilters.filter((filter) => filter.label !== "").every(
      (filter) => filter.selectedValue !== ""
    );
  };

  const onFetchLegacyReports = async (): Promise<void> => {
    setIsLoading(true);

    try {
      const response = await getAxios("/reports/legacy", "GET");
      const data = await response.data;

      setLegacyReports(data);

      setCurrentReport(data[0]);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onFetchFilters = async (id: number) => {
    setIsLoading(true);

    try {
      const response = await getAxios(`/reports/legacy/${id}/filters`, "GET");

      const data: FilterData = await response.data;

      if (typeof data === "string") {
        setFilters([]);

        return;
      }

      const relationData: RelationData[] = [];

      for (let i = 1; i <= 8; i++) {
        //T filters
        const labelTKey = `labelT${i}` as keyof typeof data;
        const typeTKey = `typeT${i}` as keyof typeof data;

        relationData.push({
          paramName: `t${i}`,
          type: "T",
          label: data[labelTKey] as string,
          value: data[typeTKey] as string,
          selectedValue: data[typeTKey] === "D" ? new Date() : "",
        });
      }

      for (let i = 1; i <= 8; i++) {
        //Dropdown Filters
        const labelDKey = `labelD${i}` as keyof typeof data;
        const valuesDKey = `valuesD${i}` as keyof typeof data;

        console.log(data[labelDKey]);
        console.log("values: ", valuesDKey);
        console.log("data: ", data[valuesDKey]);

        relationData.push({
          paramName: `d${i}`,
          type: "D",
          label: data[labelDKey] as string,
          value:
            data[valuesDKey] === null || data[valuesDKey].length === 0
              ? [{ id: 0, description: "--", sortOrder: 1, isActive: true }]
              : (data[valuesDKey] as DropdownValue[]),
          selectedValue:
            data[valuesDKey] === null || data[valuesDKey].length === 0
              ? "0"
              : ((data[valuesDKey] as DropdownValue[])[0]
                  .id as unknown as string),
        });
      }

      setFilters(relationData);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onFetchReport = async (id: number): Promise<void> => {
    const obj = {};

    for (const [index, value] of filters
      .filter((val) => val.type !== "D")
      .entries()) {
      if (value.value === "D") {
        const fDate = formatDateHyphen(value.selectedValue as Date);

        (obj as any)[`T${index + 1}Param`] = fDate;
      } else {
        (obj as any)[`T${index + 1}Param`] = value.selectedValue;
      }
    }

    for (const [index, value] of filters
      .filter((val) => val.type !== "T")
      .entries()) {
      (obj as any)[`D${index + 1}Param`] = value.selectedValue;
    }

    const {
      T1Param,
      T2Param,
      T3Param,
      T4Param,
      T5Param,
      T6Param,
      T7Param,
      T8Param,
      D1Param,
      D2Param,
      D3Param,
      D4Param,
      D5Param,
      D6Param,
      D7Param,
      D8Param,
    } = obj as any;

    try {
      console.log(obj);

      const response = await getAxios(
        `/reports/legacy/${id}`,
        "GET",
        undefined,
        {
          startIndex: 0,
          pageSize: 25,
          T1Param: T1Param,
          T2Param: T2Param,
          T3Param: T3Param,
          T4Param: T4Param,
          T5Param: T5Param,
          T6Param: T6Param,
          T7Param: T7Param,
          T8Param: T8Param,
          D1Param: D1Param,
          D2Param: D2Param,
          D3Param: D3Param,
          D4Param: D4Param,
          D5Param: D5Param,
          D6Param: D6Param,
          D7Param: D7Param,
          D8Param: D8Param,
        }
      );

      setLegacyReport(await response.data);
    } catch (error) {
      console.error(error);
    } finally {
      console.log("finally");
    }
  };

  useEffect(() => {
    onFetchLegacyReports();
  }, []);

  useEffect(() => {
    if (currentReport) {
      onFetchFilters(currentReport.id);
    }
  }, [currentReport]);

  return (
    <>
      <CustomBreadCrumbs path={`Home/Reports`} />
      <PageHeader title="Legacy Reports" />
      <Box width={350} display="flex" alignItems="center" gap={1}>
        <CustomAutocomplete
          disabled={isLoading}
          name="reports"
          label={{
            text: "Select report",
          }}
          onChange={(event, value) => {
            setCurrentReport(value);
            setFilters([]);
            setLegacyReport(null);
          }}
          value={currentReport}
          options={legacyReports}
        />
      </Box>
      {currentReport && (
        <LegacyFilters
          values={values}
          onChangeInput={onChangeInput}
          onChangeSelect={onChangeSelect}
          onChangeDate={onChangeDate}
          filters={filters.filter((value) => value.label !== "").slice(0, 4)}
        />
      )}
      <Box mt={4}>
        {currentReport && (
          <Card sx={{ borderRadius: 4 }}>
            <CardContent>
              <Stack direction="row" spacing={2}>
                <Button
                  disabled={!isGenerateButtonDisabled()}
                  width={120}
                  height={35}
                  startIcon={<RefreshOutlined />}
                  label="Generate"
                  variant="outlined"
                  onClick={() => onFetchReport(currentReport.id)}
                />
                <Button
                  disabled={!(filters.length > 4)}
                  onClick={() => setIsDrawerOpen(true)}
                  width={106}
                  height={35}
                  startIcon={
                    <SvgIcon>
                      <Icon path={mdiFilterOutline} />
                    </SvgIcon>
                  }
                  label="Filter"
                />
              </Stack>
              {legacyReport === null ? (
                <></>
              ) : (
                <LegacyReportGrid
                  page={startIndex}
                  pageSize={pageSize}
                  onChangePage={(newIndex) => onChangePage(newIndex)}
                  onChangePageSize={(newSize) => onChangePageSize(newSize)}
                  rows={legacyReport || []}
                />
              )}
            </CardContent>
          </Card>
        )}
        {currentReport && (
          <LegacyReportSidebar
            filters={filters.filter((value) => value.label !== "").slice(4)}
            isOpen={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            onChangeDate={onChangeDate}
            onChangeSelect={onChangeSelect}
            values={[]}
          />
        )}
      </Box>
    </>
  );
};
